import { useFormikContext } from "formik";
import { ConfigTabState } from "../../types";
import { Box, FormControlLabel, Stack } from "@mui/material";
import { LocalizationProvider, TimeField } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { FC } from "react";
import { PickerValue } from "@mui/x-date-pickers/internals";
import dayjs from "dayjs";
// Imported here rather than only at the app entry, so the adapter's timezone
// support is guaranteed wherever this field is rendered.
import { EXCHANGE_TIMEZONE } from "../../../../dayjsSetup";
import { useThemeMode } from "../../../../theme/atom";

const format = "HH:mm";

type Props = {
  label: React.ReactNode;
  fieldKey: keyof PickerValue;
  endBeforeStartError?: boolean;
  disabled?: boolean;
};

const TimeInput: FC<Props> = ({
  fieldKey,
  label,
  endBeforeStartError,
  disabled,
}) => {
  const { values, setFieldValue } = useFormikContext<ConfigTabState>();
  const [themeMode] = useThemeMode();
  return (
    <Stack direction="row" spacing={1}>
      <FormControlLabel
        sx={{
          margin: 0,
          flex: 1,
          // Matches LabeledInput, which the sibling rows in this column use.
          "& span": {
            flex: 1,
            textAlign: "left",
          },
        }}
        label={label}
        control={<Box />}
      />
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <TimeField
          disabled={disabled}
          error={endBeforeStartError}
          // Read and entered on the venue's clock. The stored value stays an
          // absolute instant — only its presentation moves to Tokyo.
          timezone={EXCHANGE_TIMEZONE}
          size="small"
          sx={{
            flex: 1,
            borderRadius: themeMode === "tokyoNight" ? "5px" : "12px",
            "& .Mui-disabled": {
              backgroundColor: (t) => t.palette.background.default,
              opacity: 0.6,
            },
          }}
          format={format}
          onChange={(time) => {
            if (time?.isValid()) {
              setFieldValue(fieldKey, time);
            }
          }}
          value={values[fieldKey] ? dayjs(values[fieldKey]) : null}
        />
      </LocalizationProvider>
    </Stack>
  );
};

export { TimeInput };
